/**
 * Versions Tab - displays and manages table versions
 *
 * Features:
 * - Inline upload zone for adding new versions
 * - Version list with system tags (current/previous)
 * - Tag management and version actions
 *
 * @module features/workspace/components/PreviewPanel/VersionsTab
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  GitBranch,
  Minus,
  Plus,
  Tags,
  Trash2,
  XCircle,
} from 'lucide-react';
import {
  Skeleton,
  Button,
  Badge,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  cn,
} from '@loader/ui';
import { LabelBadge, TagBadge } from '@loader/ui';
import type { ConnectorType } from '@loader/shared-types';

import { formatRelativeTime, formatFileSize, formatRowCount } from './shared';
import { ConnectorSyncZone } from './ConnectorSyncZone';
import { VersionUploadZone } from './VersionUploadZone';

import { useRpc } from '@/lib/rpc';
import { MAX_PROCESSING_STALENESS_MS } from '@/lib/constants';
import { invalidateVersionQueries } from '@/lib/queryInvalidation';
import { useTableVersionsQuery } from '@/features/tables/hooks/useTableQueries';
import { toast } from '@/lib/toast';
import { ConfirmDeleteDialog, getErrorCountLabel } from '@/components/common';
import EditVersionTagsDialog from '@/features/tables/components/dialogs/EditVersionTagsDialog';
import { VersionErrorDialog } from '@/features/tables/components/dialogs/VersionErrorDialog';

/** Version statuses that indicate processing is in progress */
const PROCESSING_STATUSES = ['queued', 'pending', 'processing'] as const;

/** Polling interval for version processing status (ms) */
const VERSION_POLL_INTERVAL_MS = 2000;

interface VersionsTabProps {
  tableId: string;
  /** Connector type if the table has an automated connector */
  connectorType?: ConnectorType | null;
  /** Pre-selected file (e.g., from drag-drop on preview panel) */
  droppedFile?: File | null;
  /** Callback when dropped file is cleared */
  onClearDroppedFile?: () => void;
  /** Whether user can edit - viewers see read-only version list */
  canEdit?: boolean;
}

export function VersionsTab({ tableId, connectorType, droppedFile, onClearDroppedFile, canEdit = true }: VersionsTabProps) {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  // Check if any version is still processing (needs polling)
  const [hasProcessingVersion, setHasProcessingVersion] = useState(false);

  // Temporary polling after sync trigger — awaits new version creation from async callback
  const [awaitingSyncVersion, setAwaitingSyncVersion] = useState(false);
  const awaitingSyncTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const handleSyncTriggered = useCallback(() => {
    setAwaitingSyncVersion(true);
    // Stop polling after 60s even if no new version appears (sync may have failed silently)
    clearTimeout(awaitingSyncTimerRef.current);
    awaitingSyncTimerRef.current = setTimeout(() => setAwaitingSyncVersion(false), 60_000);
  }, []);

  // Clear sync-awaiting timer on unmount
  useEffect(() => () => clearTimeout(awaitingSyncTimerRef.current), []);

  // Append mode state - tracks selected base version and whether a file is staged
  const [selectedBaseVersionId, setSelectedBaseVersionId] = useState<string | null>(null);
  const [isFileStaged, setIsFileStaged] = useState(false);

  const shouldPoll = hasProcessingVersion || awaitingSyncVersion;

  const { data: versions = [], isLoading, refetch } = useTableVersionsQuery(tableId, {
    enabled: !!tableId,
    // Poll while any version is processing or awaiting sync result
    refetchInterval: shouldPoll ? VERSION_POLL_INTERVAL_MS : false,
  });

  // Update polling state when versions change
  // Must include 'queued' to catch versions that fail before transitioning to 'processing'
  // Exclude stale versions (stuck for >5 min) to avoid polling forever
  useEffect(() => {
    const processing = versions.some((v) => {
      if (!PROCESSING_STATUSES.includes(v.status as typeof PROCESSING_STATUSES[number])) return false;
      // Don't poll for stale versions
      if (v.created_at && Date.now() - new Date(v.created_at).getTime() > MAX_PROCESSING_STALENESS_MS) return false;
      return true;
    });
    setHasProcessingVersion(processing);
    // Once a processing version appears, the normal polling takes over — stop sync-awaiting
    if (processing && awaitingSyncVersion) {
      setAwaitingSyncVersion(false);
      clearTimeout(awaitingSyncTimerRef.current);
    }
  }, [versions, awaitingSyncVersion]);

  // Version error dialog state
  const [errorDialogVersion, setErrorDialogVersion] = useState<{
    version_number: number;
    file_name: string;
    created_at: string;
    error_message: string;
    error_details?: Record<string, unknown> | null;
  } | null>(null);

  const [editTagsOpen, setEditTagsOpen] = useState(false);
  const [pendingEditVersion, setPendingEditVersion] = useState<{
    id: string;
    file_name: string;
    tags: Array<{ id: string; name: string; color: string | null }>;
  } | null>(null);

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{
    id: string;
    file_name: string;
    version_number: number;
    created_at: string;
    records_processed: number | null;
    file_size: number;
    system_tag: string | null;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleOpenEditTags = (version: {
    id: string;
    file_name: string;
    tags: Array<{ id: string; name: string; color: string | null }>;
  }) => {
    setPendingEditVersion(version);
    setEditTagsOpen(true);
  };

  const refreshVersions = useCallback(async () => {
    await invalidateVersionQueries(queryClient, tableId);
    refetch();
  }, [queryClient, tableId, refetch]);

  const handleTagCurrent = async (versionId: string) => {
    try {
      // @ts-expect-error - Hono RPC type inference limitation
      const res = await rpc.api.private.versions[':id']['system-tag'].$patch({
        param: { id: versionId },
        json: { system_tag: 'current' },
      });
      if (!res.ok) throw new Error('Failed to tag as Current');
      toast.version.setCurrent();
      await refreshVersions();
    } catch (error) {
      toast.version.setCurrentFailed(error instanceof Error ? error.message : undefined);
    }
  };

  const handleTagPrevious = async (versionId: string) => {
    try {
      // @ts-expect-error - Hono RPC type inference limitation
      const res = await rpc.api.private.versions[':id']['system-tag'].$patch({
        param: { id: versionId },
        json: { system_tag: 'previous' },
      });
      if (!res.ok) throw new Error('Failed to tag as Previous');
      toast.version.setPrevious();
      await refreshVersions();
    } catch (error) {
      if (error instanceof Error && error.message.includes('already')) {
        toast.version.alreadyPrevious();
      } else {
        toast.version.setPreviousFailed(error instanceof Error ? error.message : undefined);
      }
    }
  };

  const handleClearSystemTag = async (versionId: string) => {
    try {
      // @ts-expect-error - Hono RPC type inference limitation
      const res = await rpc.api.private.versions[':id']['system-tag'].$patch({
        param: { id: versionId },
        json: { system_tag: null },
      });
      if (!res.ok) throw new Error('Failed to clear system tag');
      toast.version.tagCleared();
      await refreshVersions();
    } catch (error) {
      toast.version.tagClearFailed(error instanceof Error ? error.message : undefined);
    }
  };

  /**
   * Request version deletion - opens confirmation dialog
   * Shows toast for protected/last version instead of dialog
   * Deletes immediately without confirmation for failed versions
   */
  const handleRequestDelete = async (
    version: typeof pendingDelete,
    isProtected: boolean,
    isLastVersion: boolean,
    isFailed: boolean
  ) => {
    if (isLastVersion) {
      toast.version.deleteLast();
      return;
    }
    if (isProtected) {
      toast.version.deleteProtected();
      return;
    }
    // Failed versions can be deleted without confirmation
    if (isFailed && version) {
      try {
        // @ts-expect-error - Hono RPC type inference limitation
        const res = await rpc.api.private.versions[':id'].$delete({
          param: { id: version.id },
        });
        if (!res.ok) throw new Error('Failed to delete version');
        toast.version.deleted(version.file_name);
        await refreshVersions();
      } catch (error) {
        toast.version.deleteFailed(error instanceof Error ? error.message : undefined);
      }
      return;
    }
    setPendingDelete(version);
    setDeleteDialogOpen(true);
  };

  /**
   * Confirm version deletion - called after user confirms in dialog
   */
  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      setIsDeleting(true);
      // @ts-expect-error - Hono RPC type inference limitation
      const res = await rpc.api.private.versions[':id'].$delete({
        param: { id: pendingDelete.id },
      });
      if (!res.ok) throw new Error('Failed to delete version');
      toast.version.deleted(pendingDelete.file_name);
      setDeleteDialogOpen(false);
      setPendingDelete(null);
      await refreshVersions();
    } catch (error) {
      toast.version.deleteFailed(error instanceof Error ? error.message : undefined);
    } finally {
      setIsDeleting(false);
    }
  };

  /**
   * Handle successful version upload
   * Refreshes the versions list to show the new version
   */
  const handleUploadComplete = () => {
    setSelectedBaseVersionId(null);
    setIsFileStaged(false);
    void refreshVersions();
  };

  if (isLoading) {
    return (
      <div className="space-y-2 py-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  // Upload or sync zone depending on whether table has a connector
  const uploadOrSyncZone = canEdit && (
    connectorType ? (
      <ConnectorSyncZone tableId={tableId} connectorType={connectorType} onSyncTriggered={handleSyncTriggered} />
    ) : (
      <VersionUploadZone
        tableId={tableId}
        preselectedFile={droppedFile}
        onClearPreselectedFile={onClearDroppedFile}
        onUploadComplete={handleUploadComplete}
        selectedBaseVersionId={selectedBaseVersionId}
        onClearBaseVersion={() => setSelectedBaseVersionId(null)}
        onFileStagedChange={setIsFileStaged}
      />
    )
  );

  // Empty state - show upload/sync zone for editors/owners
  if (versions.length === 0) {
    return (
      <div className="space-y-4 py-2">
        {uploadOrSyncZone}
        <div className="flex h-24 items-center justify-center text-muted-foreground text-sm">
          No versions yet
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-2 py-2">
        {uploadOrSyncZone}

        {/* Versions list */}
        {versions.map((version) => {
          const hasCurrentTag = version.system_tag === 'current';
          const hasPreviousTag = version.system_tag === 'previous';
          const isProtected = hasCurrentTag || hasPreviousTag;
          const isFailed = version.status === 'failed';
          const isProcessing = PROCESSING_STATUSES.includes(version.status as typeof PROCESSING_STATUSES[number]);
          const customTags = Array.isArray(version.tags) ? version.tags : [];
          // Show actions only for completed versions and when user can edit
          const showActions = canEdit && !isFailed && !isProcessing;
          const hasTags = hasCurrentTag || hasPreviousTag || customTags.length > 0;
          // Check if this version is selected as base for append
          const isSelectedAsBase = selectedBaseVersionId === version.id;
          return (
            <div
              key={version.id}
              className={cn(
                'rounded-lg border p-3 transition-all duration-300',
                isSelectedAsBase && 'border-primary/30 bg-primary/5'
              )}
            >
              <div className="flex gap-2">
                {/* Left content column */}
                <div className="flex w-0 flex-1 flex-col gap-1">
                  <div className="flex items-center gap-2 transition-all duration-300">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="truncate text-sm cursor-default">
                          <span className="font-semibold">V{version.version_number}</span>
                          <span className="text-muted-foreground"> · {version.file_name}</span>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        <p className="text-xs">{version.file_name}</p>
                      </TooltipContent>
                    </Tooltip>
                    {/* Processing badge */}
                    <Badge
                      variant="secondary"
                      className={`transition-all duration-300 ${isProcessing ? 'scale-100 opacity-100' : 'w-0 scale-0 opacity-0'}`}
                    >
                      processing
                    </Badge>
                  </div>
                  {/* Tags row */}
                  <div className="flex items-center gap-1.5">
                    {hasTags ? (
                      <>
                        {hasCurrentTag && <LabelBadge label="current" />}
                        {hasPreviousTag && <LabelBadge label="previous" />}
                        {customTags.map((tag) => (
                          <TagBadge key={tag.id} name={tag.name} color={tag.color} size="sm" />
                        ))}
                      </>
                    ) : (
                      <span className="text-muted-foreground text-xs">No tags</span>
                    )}
                  </div>
                  {/* Stats row */}
                  <div className="text-muted-foreground text-xs">
                    <span className="truncate">
                      {formatRowCount(version.records_processed ?? 0)} rows
                      {' · '}
                      {formatFileSize(version.parquet_size || version.file_size)}
                      {' · '}
                      {formatRelativeTime(version.created_at)}
                    </span>
                  </div>
                </div>
                {/* Right actions */}
                <div className="flex items-start gap-1 transition-opacity duration-300">
                  {/* Append button - shown when file is staged and version is completed */}
                  {isFileStaged && showActions && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant={isSelectedAsBase ? 'default' : 'outline'}
                          size="icon"
                          className={cn(
                            'size-7',
                            !isSelectedAsBase && 'border-primary/30 bg-primary/10 hover:bg-primary/20'
                          )}
                          onClick={() => setSelectedBaseVersionId(isSelectedAsBase ? null : version.id)}
                        >
                          {isSelectedAsBase ? (
                            <Minus className="size-3.5" />
                          ) : (
                            <Plus className="size-3.5 text-primary" />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {isSelectedAsBase ? 'Clear append selection' : 'Append to this version'}
                      </TooltipContent>
                    </Tooltip>
                  )}
                  {/* Tags menu - system tags + custom tags in one dropdown */}
                  {showActions && (
                    <DropdownMenu>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="icon" className="size-7">
                              <Tags className="size-3.5" />
                            </Button>
                          </DropdownMenuTrigger>
                        </TooltipTrigger>
                        <TooltipContent>Tags</TooltipContent>
                      </Tooltip>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleTagCurrent(version.id)} disabled={hasCurrentTag}>
                          <GitBranch className="mr-2 size-4" />
                          Set as Current
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleTagPrevious(version.id)} disabled={hasPreviousTag}>
                          <GitBranch className="mr-2 size-4" />
                          Set as Previous
                        </DropdownMenuItem>
                        {isProtected && (
                          <DropdownMenuItem onClick={() => handleClearSystemTag(version.id)}>
                            <XCircle className="mr-2 size-4" />
                            Clear System Tag
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleOpenEditTags({ id: version.id, file_name: version.file_name, tags: version.tags })}>
                          <Tags className="mr-2 size-4" />
                          Edit Custom Tags
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  {/* Error badge - shown for failed versions */}
                  {isFailed && version.error_message ? (
                    <Badge
                      variant="destructive"
                      className="cursor-pointer h-7 transition-all duration-300"
                      onClick={() => setErrorDialogVersion({
                        version_number: version.version_number ?? 0,
                        file_name: version.file_name,
                        created_at: version.created_at,
                        error_message: version.error_message ?? '',
                        error_details: version.error_details as Record<string, unknown> | null,
                      })}
                    >
                      {getErrorCountLabel(version.error_message, version.error_details)}
                    </Badge>
                  ) : isFailed ? (
                    <Badge
                      variant="destructive"
                      className="h-7 transition-all duration-300"
                    >
                      failed
                    </Badge>
                  ) : null}
                  {/* Delete - editors/owners only */}
                  {canEdit && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="icon"
                          className="size-7"
                          onClick={() => handleRequestDelete(
                            {
                              id: version.id,
                              file_name: version.file_name,
                              version_number: version.version_number ?? 0,
                              created_at: version.created_at,
                              records_processed: version.records_processed ?? null,
                              file_size: version.file_size,
                              system_tag: version.system_tag,
                            },
                            isProtected,
                            versions.length === 1,
                            isFailed
                          )}
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {versions.length === 1 ? 'Cannot delete last version' : isProtected ? 'Cannot delete protected version' : 'Delete version'}
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <EditVersionTagsDialog
        open={editTagsOpen}
        onOpenChange={setEditTagsOpen}
        version={pendingEditVersion}
        onSaved={() => refreshVersions()}
      />

      {/* Delete confirmation dialog */}
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Version"
        isLoading={isDeleting}
        onConfirm={handleConfirmDelete}
      />

      {/* Version error dialog */}
      <VersionErrorDialog
        open={!!errorDialogVersion}
        onOpenChange={(open) => { if (!open) setErrorDialogVersion(null); }}
        version={errorDialogVersion}
      />
    </>
  );
}
